import { useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '@shared/redux';
import { viewFiltersActions, viewFiltersSelectors, type ViewScope } from '@shared/redux/ducks/view';
import type { TeamSort } from '../lib/types';

const PAGE = 'organizationTeam';
const VIEW_ID = 'teamTable';

export enum TeamParamsEnum {
  SORT = 'sort',
  SEARCH = 'search',
}

export const DEFAULT_TEAM_SEARCH = '';
export const DEFAULT_TEAM_SORT: TeamSort = '-created_at';
export const DEFAULT_SORT_BY: TeamSort = '-created_at';

export interface TeamFiltersState {
  search: string;
  sort: TeamSort;
}

const DEFAULT_TEAM_FILTERS: TeamFiltersState = {
  search: DEFAULT_TEAM_SEARCH,
  sort: DEFAULT_TEAM_SORT,
};

const buildScope = (organizationId: number): ViewScope => ({
  page: PAGE,
  organizationId,
  viewId: VIEW_ID,
});

export const useTeamFilters = (organizationId: number) => {
  const dispatch = useAppDispatch();
  const scope = useMemo(() => buildScope(organizationId), [organizationId]);

  const persisted = useAppSelector(
    viewFiltersSelectors.selectFiltersByScope(scope),
  ) as Partial<TeamFiltersState> | null;

  const filters: TeamFiltersState = useMemo(
    () => ({
      ...DEFAULT_TEAM_FILTERS,
      ...(persisted ?? {}),
    }),
    [persisted],
  );

  const handleSearch = (search: string) => {
    if (search === filters.search) return;

    dispatch(
      viewFiltersActions.updateFiltersForScope({
        scope,
        partialFilters: { search },
      }),
    );
  };

  const handleSort = (sort: TeamSort) => {
    if (sort === filters.sort) return;

    dispatch(
      viewFiltersActions.updateFiltersForScope({
        scope,
        partialFilters: { sort },
      }),
    );
  };

  return {
    search: filters.search,
    sort: filters.sort,
    handleSearch,
    handleSort,
  };
};
